// Simple mode actions (§11.19): choose a filter / change its strength (one undo step each; a strength drag
// is one step on release), and the Save buttons.
import { useEditor } from '../state/editor';
import { useLibrary, type LibraryPhoto } from '../state/library';
import { useSimple } from '../state/simple';
import { applyFilter, getPath, SIMPLE_FILTERS, filterPaths } from './simpleFilters';
import { exportWithExtras } from './exportHelpers';
import { BUILT_IN_EXPORT_PRESETS } from './exportPresets';
import { optionsFor } from './batchExport';
import { loadMetadata } from './appSettings';
import { sanitizeName, stripExt } from '../io/rename';
import type { ExportPreset, Settings } from '../model/types';

function activePhoto(): LibraryPhoto | null {
  const lib = useLibrary.getState();
  return lib.photos.find((p) => p.id === lib.activeId) ?? null;
}

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The settings a filter starts from: the recorded base while the filter is still in place, else now. */
function baseFor(id: string, cur: Settings): Settings {
  const rec = useSimple.getState().filter[id];
  return rec && sameJson(rec.applied, cur) ? rec.base : cur;
}

/** Applies `filterId` (or removes the current filter when null) to the active photo at `strength` 0..1. */
export function chooseFilter(filterId: string | null, strength = 1) {
  const p = activePhoto();
  if (!p) return;
  const ed = useEditor.getState();
  const cur = ed.get(p.id);
  const base = baseFor(p.id, cur);
  if (!filterId) {
    ed.update(p.id, (s) => void Object.assign(s, structuredClone(base)));
    useSimple.setState((st) => ({ filter: { ...st.filter, [p.id]: undefined } }));
    return;
  }
  const f = SIMPLE_FILTERS.find((x) => x.id === filterId)!;
  const applied = applyFilter(base, f, strength);
  ed.update(p.id, (s) => void Object.assign(s, structuredClone(applied)));
  useSimple.setState((st) => ({
    filter: { ...st.filter, [p.id]: { id: filterId, base, strength, applied: ed.get(p.id) } },
  }));
}

/** Strength slider: recomputes the filter's own fields from its base, keeping everything else. */
export function setFilterStrength(strength: number, final: boolean) {
  const p = activePhoto();
  if (!p) return;
  const rec = useSimple.getState().filter[p.id];
  if (!rec) return;
  const f = SIMPLE_FILTERS.find((x) => x.id === rec.id)!;
  const target = applyFilter(rec.base, f, strength);
  const ed = useEditor.getState();
  ed.update(
    p.id,
    (s) => {
      for (const path of filterPaths(f)) {
        if (path === 'curve.master') s.curve.master = structuredClone(target.curve.master);
        else {
          const keys = path.split('.');
          const last = keys.pop()!;
          (keys.reduce<unknown>((a, k) => (a as Record<string, unknown>)[k], s) as Record<string, number>)[
            last
          ] = getPath(target, path);
        }
      }
    },
    { commit: final },
  );
  useSimple.setState((st) => ({
    filter: { ...st.filter, [p.id]: { ...rec, strength, applied: ed.get(p.id) } },
  }));
}

/** The Save buttons (§11.19 step 5). */
export type SaveTarget = 'instagram' | 'whatsapp' | 'menu' | 'delivery' | 'png' | 'original';
const WHATSAPP: ExportPreset = {
  ...BUILT_IN_EXPORT_PRESETS.find((p) => p.id === 'web-2048')!,
  id: 'whatsapp-1600',
  name: 'WhatsApp 1600',
  nameEs: 'WhatsApp 1600',
  quality: 80,
  size: { mode: 'longEdge', w: 1600 },
  subfolder: 'whatsapp',
};
export function presetForSave(t: SaveTarget): ExportPreset {
  // Simple mode has no metadata control, so every save strips GPS, Original size included (B3).
  return { ...savePreset(t), metadata: 'stripGps' };
}

function savePreset(t: SaveTarget): ExportPreset {
  const byId = (id: string) => BUILT_IN_EXPORT_PRESETS.find((p) => p.id === id)!;
  return t === 'whatsapp'
    ? WHATSAPP
    : byId(
        {
          instagram: 'instagram-1080',
          menu: 'menu-board-4k',
          delivery: 'delivery-listing',
          png: 'transparent-png',
          original: 'full-res-jpeg',
        }[t],
      );
}

/** Exports the active photo for `t`: into the folder's exports/ when a folder is open, else a download. */
export async function simpleSave(t: SaveTarget): Promise<string> {
  const p = activePhoto();
  if (!p) throw new Error('no photo');
  const preset = presetForSave(t);
  const settings = useEditor.getState().get(p.id);
  const opts = optionsFor(preset, { watermark: null, watermarkAll: false, metadataOverride: null });
  const res = await exportWithExtras(p, settings, opts, await loadMetadata());
  const ext = preset.format === 'jpeg' ? 'jpg' : preset.format === 'png' ? 'png' : 'tif';
  const name = `${sanitizeName(stripExt(p.name)) || 'photo'}_${preset.subfolder}.${ext}`;
  const folder = useLibrary.getState().folder;
  if (folder?.mode === 'fsa') return folder.writeExport(preset.subfolder, name, res.bytes);
  const mime = ext === 'jpg' ? 'image/jpeg' : ext === 'png' ? 'image/png' : 'image/tiff';
  const url = URL.createObjectURL(new Blob([res.bytes as Uint8Array<ArrayBuffer>], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}
